// biome-ignore lint/style/useNodejsImportProtocol: <explanation>
import { createHash } from 'crypto';
// cspell:disable
import { DatetimeFormatPlaceHolder, formatDateTimeRaw, getFullDayOfWeekName } from '@lemonade-hq/lemonation';
import { capitalize, getDateComponents, interpolate } from '@lemonade-hq/ts-helpers';
import type { NextParsedUrlQuery } from 'next/dist/server/request-meta';
import { categories } from './categories';

const ENCODED_VALUE_QUERY_PARAM = 'v';
const ENCODED_HASH_QUERY_PARAM = 's';
const ENCODED_VALUE_DELIMITER = '|';
const MD5_SALT = 'babbi';
const NO_CAUSE = 'unavailable';

enum TemplateInterpolation {
  Cause = 'cause',
  City = 'city',
  Date = 'date',
  Day = 'day',
  Name = 'name',
  Product = 'product',
}

const TEMPLATES = {
  withCause: `{${TemplateInterpolation.Date}} was just another {${TemplateInterpolation.Day}} in {${TemplateInterpolation.City}}. The glow from your phone offered a comforting warmth. "Sleek move, [{${TemplateInterpolation.Name}}](interpolatedName)," you quietly congratulated yourself. You now have a new Lemonade {${TemplateInterpolation.Product}} policy! A small, satisfied smile curved your lips.
    [dummy](firstLineBreak)But this seemingly unremarkable act held deeper significance that extended far beyond yourself.[dummy](lineBreak)
    That day, you chose to donate your unused premiums to [{${TemplateInterpolation.Cause}}](interpolatedName), and with that, unknowingly triggered a ripple of goodness, impacting the lives of others you’ve never even met.`,

  noCause: `{${TemplateInterpolation.Date}} was just another {${TemplateInterpolation.Day}} in {${TemplateInterpolation.City}}. The glow from your phone offered a comforting warmth. "Sleek move, [{${TemplateInterpolation.Name}}](interpolatedName)," you quietly congratulated yourself. You now have a new Lemonade {${TemplateInterpolation.Product}} policy! A small, satisfied smile curved your lips.
    [dummy](lineBreak)But this seemingly unremarkable act held deeper significance that extended far beyond yourself, unknowingly triggering a ripple of goodness, impacting the lives of others you’ve never even met.`,
  guest: `March 15, 2023 was just another Wednesday in NYC. The glow from Amy’s phone offered a comforting warmth. "Sleek move, Amy," she quietly congratulated herself. You now have a new Lemonade policy! A small, satisfied smile curved her lips.[dummy](lineBreak)
    But this seemingly unremarkable act held deeper significance that extended far beyond herself, unknowingly triggering a ripple of goodness, impacting the lives of others she’s never even met.`,
};

export function getGiveback2023IntroText(query: NextParsedUrlQuery): {
  readonly content: string;
  readonly causeCategoryName?: string;
  readonly causeName?: string;
  readonly isGuest: boolean;
} {
  const base64EncodedValue = query[ENCODED_VALUE_QUERY_PARAM] as string | undefined;
  const receivedHash = query[ENCODED_HASH_QUERY_PARAM] as string | undefined;

  if (!base64EncodedValue || !receivedHash) return { content: TEMPLATES.guest, isGuest: true };

  const decodedValue = Buffer.from(base64EncodedValue as string, 'base64').toString('utf-8');

  const shasum = createHash('md5');
  shasum.update(decodedValue + MD5_SALT);
  const calculatedHash = shasum.digest('hex');
  if (calculatedHash !== receivedHash) return { content: TEMPLATES.guest, isGuest: true };

  const splitValue = decodedValue.split(ENCODED_VALUE_DELIMITER);
  const [rawName, rawCity, rawDate, rawCause, rawProduct] = splitValue;
  const rawDateComponents = getDateComponents(new Date(rawDate));

  const name = capitalize(rawName);
  const city = rawCity
    .replaceAll('"', '') // remove quotes inside the names
    .replaceAll(/ \(.*\)/g, '') // remove parentheses and content inside
    .replaceAll(/, .*/g, ''); // remove commas and content after
  const date = formatDateTimeRaw(
    rawDateComponents,
    'en-US',
    `${DatetimeFormatPlaceHolder.FullMonthName} ${DatetimeFormatPlaceHolder.DayNotPadded}, ${DatetimeFormatPlaceHolder.Year}`,
  );
  const cause = rawCause
    .toLowerCase()
    .replaceAll('"', '')
    .replaceAll(/ \(.*\)/g, '')
    .replaceAll('hôpitaux de paris ', '')
    .replaceAll('once earth one ocean', 'one earth - one ocean')
    .replaceAll('one earth one ocean', 'one earth - one ocean')
    .replaceAll('spca international', 'spcai')
    .replaceAll('unicef', 'unicef usa')
    .replaceAll('charity:water', 'charity: water');
  const product = rawProduct;
  const causeCategory = categories.find(category =>
    category.subCategories?.some(subCategory => subCategory.name?.toLowerCase() === cause.toLowerCase()),
  );

  if (!causeCategory && rawCause !== NO_CAUSE) return { content: TEMPLATES.guest, isGuest: true };

  const categoryName = causeCategory?.formattedName;
  const day = getFullDayOfWeekName('en-US', rawDateComponents.dayOfWeek);

  const selectedTemplate =
    !rawCause || rawCause === NO_CAUSE || rawCause.trim().length === 0 ? TEMPLATES.noCause : TEMPLATES.withCause;

  return {
    content: interpolate(selectedTemplate, {
      [TemplateInterpolation.Name]: name,
      [TemplateInterpolation.City]: city,
      [TemplateInterpolation.Date]: date,
      [TemplateInterpolation.Cause]: categoryName,
      [TemplateInterpolation.Product]: product,
      [TemplateInterpolation.Day]: day,
    }),
    causeName: cause,
    causeCategoryName: causeCategory?.name,
    isGuest: false,
  };
}
